// Analytics Viz Roadmap — Phase 1. The ONE sanctioned binding between the SVG
// chart kit and the 7-theme CSS variables. An SVG fill/stroke set to a
// `var(--c-…)` string paints THROUGH the live CSS cascade, so the instant
// useTheme flips `data-theme` on <html> the browser re-resolves every var() and
// the whole chart re-tints — no JS, no redraw, no subscription. This is what
// makes the kit theme-safe by construction.
//
// HARD RULE: a chart must NEVER emit a Tailwind `/NN` opacity class
// (e.g. bg-brand-600/40). Those compile to a separate escaped selector and
// render raw hex on the 6 non-default themes unless individually retargeted in
// index.css. For translucency use `soft()` (color-mix on the resolved var),
// which stays theme-safe.

// The 4 theme-safe categorical series tokens (each defined in all 7 theme blocks).
export const SERIES = {
  accent: 'var(--c-accent)', // primary / neutral series
  up: 'var(--c-up)', // positive (advanced, accepted, on-pace)
  warn: 'var(--c-warn)', // caution (ghosted, stalled, behind)
  down: 'var(--c-down)', // negative (rejected, declined)
};

export const GRID = 'var(--c-border-subtle)'; // gridlines
export const AXIS = 'var(--c-border)'; // axis lines + ticks
export const LABEL = 'var(--c-text-3)'; // secondary axis/label text
export const LABEL_STRONG = 'var(--c-text-2)'; // emphasized label / value text
export const TRACK = 'var(--c-surface-2)'; // empty track / muted (under-powered) fill
export const FONT = 'var(--c-font)'; // chart text font (matches the active theme)

// Translucent variant of a theme token, theme-safe via color-mix on the resolved
// var (area fills, hover halos, heatmap ramp). pct = opacity-equivalent 0..100.
export function soft(token, pct = 20) {
  return `color-mix(in srgb, ${token} ${pct}%, transparent)`;
}

// ── T258(4) — non-text contrast (WCAG 1.4.11, 3:1) ──────────────────────────
//
// Measured across all seven themes, a translucent accent fill on the card
// surface CANNOT carry a 3:1 boundary at any opacity that still reads as soft:
//
//   soft(accent, 30)  light 1.55  cyberpunk 1.48  midnight 1.57   ← shipped
//   soft(accent, 50)  light 2.14  cyberpunk 2.23  midnight 2.33
//   soft(accent, 70)  light 3.07  cyberpunk 3.37  midnight 3.45   ← barely, and
//                                                    no longer "soft" at all
//
// The full token as a 1px OUTLINE does clear it everywhere — 5.47:1 at worst
// (light), 18.1:1 at best. So a meaning-bearing translucent shape gets both:
// the fill says which category it is, the stroke carries the contrast.
export const SOFT_FLOOR = 50;

/** Translucent fill for a meaning-bearing shape — never below the floor. */
export function softFill(token, pct = SOFT_FLOOR) {
  return soft(token, Math.max(SOFT_FLOOR, pct));
}
